import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Divider
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import TransactionWarningDialog from './warning';
import InfoIcon from '@mui/icons-material/Info';
import apiService from './api'; // Replace with your actual API service import

const Confirm = ({ 
  open, 
  onClose, 
  transactionData, 
  onConfirm 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [warningInfo, setWarningInfo] = useState(null);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningData, setWarningData] = useState(null);

  useEffect(() => {
    // If dialog is open and we have transaction data, fetch member information
    if (open && transactionData) {
      fetchMemberByPhone();
    }
  }, [open, transactionData]);

  useEffect(() => {
    if (!open) {
      // Reset warning states when dialog closes
      setWarningInfo(null);
      setShowWarningConfirm(false);
      setShowWarningDialog(false); // NEW
      setWarningData(null); // NEW
    }
  }, [open]);

  const fetchMemberByPhone = async () => {
    if (!transactionData?.phoneNo) return;
    
    setLoading(true);
    setError(null);
    setWarningInfo(null);
    
    try {
      // Extract phone number and clean it
      let phone = transactionData.phoneNo;
      
      // Remove all spaces, dashes, and other non-digit characters except +
      phone = phone.replace(/[\s\-\(\)\.]/g, '');
      
      // Handle different phone number formats
      if (phone.startsWith('+254')) {
        // +254 format - remove the +
        phone = phone.substring(1);
      } else if (phone.startsWith('0')) {
        // 0 format - convert to 254
        phone = '254' + phone.substring(1);
      } else if (phone.startsWith('254')) {
        // Already in 254 format - keep as is
        phone = phone;
      } else if (phone.length === 9) {
        // 9 digits - assume it's missing country code and leading 7/1
        phone = '254' + phone;
      }
      
      // Validate phone number length (should be 12 digits for 254 format)
      if (phone.length !== 12 || !phone.startsWith('254')) {
        setError(`Invalid phone number format: ${transactionData.phoneNo}`);
        setMemberData(null);
        return;
      }
      
      // Additional validation for Kenyan mobile numbers
      const validPrefixes = ['2547', '2541', '2570']; // Safaricom, Airtel, Telkom
      const hasValidPrefix = validPrefixes.some(prefix => phone.startsWith(prefix));
      
      if (!hasValidPrefix) {
        setError(`Invalid Kenyan mobile number: ${transactionData.phoneNo}`);
        setMemberData(null);
        return;
      }
      
      console.log(`Original phone: ${transactionData.phoneNo}, Formatted phone: ${phone}`);
      
      // Use the correct method name from api.js
      const data = await apiService.checkMemberByPhone(phone);
      
      if (!data.found || !data.member) {
        setError('No member found with this phone number');
        setMemberData(null);
      } else {
        // Map the backend response structure to what the component expects
        setMemberData({
          id: data.member.id,
          name: data.member.name,
          member_id: data.member.member_id,
          phone: data.member.phone,
          groupName: data.member.group?.name || 'No Group',
          group: data.member.group
        });

        // Check for warning information in the response
        if (data.warning) {
          setWarningInfo({
            message: data.message || 'Please review the information below before proceeding.',
            warning: data.warning,
            activePeriod: data.active_period,
            memberName: data.member_name
          });
        }
      }
    } catch (err) {
      console.error('Error fetching member:', err);
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!memberData || !transactionData) return;
    
    // If there's existing warning info and user hasn't acknowledged it yet, show warning confirmation
    if (warningInfo && !showWarningConfirm) {
      setShowWarningConfirm(true);
      return;
    }
  
    // NEW: Check for warnings before proceeding
    if (!showWarningDialog && !warningData) {
      try {
        setLoading(true);
        const warningCheck = await apiService.checkTransactionWarning(
          transactionData.mpesaRef
        );
        
        if (warningCheck.has_warnings) {
          setWarningData(warningCheck.warnings);
          setShowWarningDialog(true);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Warning check failed:', err);
        // Continue with confirmation even if warning check fails
      } finally {
        setLoading(false);
      }
    }
  
    // Proceed with confirmation
    try {
      setLoading(true);
      const result = await onConfirm({
        transaction_id: transactionData.mpesaRef,
        member_id: memberData.id
      });
  
      if (result && result.warning) {
        setWarningInfo({
          message: result.message || 'Payment confirmed with warning.',
          warning: result.warning,
          isPostConfirm: true
        });
      } else {
        onClose();
      }
    } catch (err) {
      setError(`Failed to confirm payment: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleWarningProceed = () => {
    setShowWarningDialog(false);
    setWarningData(null);
    // Continue with confirmation
    handleConfirm();
  };
  
  const handleWarningCancel = () => {
    setShowWarningDialog(false);
    setWarningData(null);
  };

  const handleProceedWithWarning = () => {
    setShowWarningConfirm(false);
    handleConfirm();
  };

  const handleCancelWarning = () => {
    setShowWarningConfirm(false);
    // Optionally close the dialog or let user review the warning
  };

  const formatPeriodDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}    
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ 
        bgcolor: '#e91e63', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box display="flex" alignItems="center">
          <Typography variant="h6">
            Assign Payment to Member
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {/* Transaction Details Section */}
        <Box sx={{ 
          bgcolor: 'background.default', 
          p: 2, 
          borderRadius: 1,
          mb: 3
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Transaction Details
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            <Box sx={{ mr: 4, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">M-Pesa Ref:</Typography>
              <Typography variant="body1">{transactionData?.mpesaRef}</Typography>
            </Box>
            
            <Box sx={{ mr: 4, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Amount:</Typography>
              <Typography variant="body1">KES {transactionData?.amount}</Typography>
            </Box>
            
            <Box sx={{ mr: 4, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Phone:</Typography>
              <Typography variant="body1">{transactionData?.phoneNo}</Typography>
            </Box>
            
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="textSecondary">Date:</Typography>
              <Typography variant="body1">{transactionData?.time}</Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Warning Section - Show if there's a warning */}
        {warningInfo && !showWarningConfirm && (
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Important Notice
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {warningInfo.message}
              </Typography>
              {warningInfo.activePeriod && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>Group Period:</strong> {formatPeriodDate(warningInfo.activePeriod.period_start)} - {formatPeriodDate(warningInfo.activePeriod.period_end)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Period For:</strong> {warningInfo.activePeriod.period_for}
                  </Typography>
                </Box>
              )}
              {!warningInfo.isPostConfirm && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  You can proceed with the confirmation or cancel to address this first.
                </Typography>
              )}
            </Alert>
          </Box>
        )}

        {/* Warning Confirmation Dialog Content */}
        {showWarningConfirm && (
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity="warning" 
              icon={<WarningIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Confirm with Warning
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Are you sure you want to proceed with this payment confirmation despite the warning?
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {warningInfo.warning}
              </Typography>
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                color="inherit" 
                onClick={handleCancelWarning}
              >
                Review Warning
              </Button>
              <Button 
                variant="contained" 
                color="warning" 
                onClick={handleProceedWithWarning}
              >
                Proceed Anyway
              </Button>
            </Box>
            
            <Divider sx={{ my: 2 }} />
          </Box>
        )}
        
        {/* Member Information Section */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          Member Information
        </Typography>
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : memberData ? (
          <Box sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 1, 
            p: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                <strong>Name:</strong> {memberData.name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <GroupIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                <strong>Group:</strong> {memberData.groupName}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BadgeIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                <strong>Member ID:</strong> {memberData.member_id}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Phone:</strong> {memberData.phone}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Alert severity="info">No member information available</Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          CANCEL
        </Button>
        <Button 
          variant="contained" 
          color={warningInfo && !warningInfo.isPostConfirm ? "warning" : "primary"}
          onClick={handleConfirm}
          disabled={loading || !memberData || showWarningConfirm}
          startIcon={warningInfo && !warningInfo.isPostConfirm ? <WarningIcon /> : null}
        >
          {warningInfo && !warningInfo.isPostConfirm ? 'CONFIRM WITH WARNING' : 'CONFIRM PAYMENT'}
        </Button>
      </DialogActions>
      {/* Warning Dialog */}
      <TransactionWarningDialog
        open={showWarningDialog}
        onClose={handleWarningCancel}
        onProceed={handleWarningProceed}
        warnings={warningData}
        transactionType="confirmation"
        loading={loading}
      />
    </Dialog>
  );
};

export default Confirm;